/* eslint-disable import/no-default-export, import/order, max-lines, max-lines-per-function, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { useRef, useState, useEffect } from "react";
import { cn } from "tailwind-variants";
import CheckCircleBoldIcon from "assets/icons/check-circle-bold.svg";
import ModalCloseIcon from "assets/icons/modal-close.svg";
import "./CanvaModal.scss";
import { renderList } from "common/vue/render-list";
import { type canva } from "common/utils/models/canva.types";
import { type createJotaiVuexFacadeForReact } from "common/vue/createJotaiVuexFacadeForReact";

type CanvaStore = ReturnType<typeof createJotaiVuexFacadeForReact>;

type CanvaModalProps = {
  store: CanvaStore;
  className?: string;
};

export default function CanvaModal({ store, ...restProps }: CanvaModalProps) {
  store.useStore();
  const modalRef = useRef<HTMLDivElement>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  function continuation(): string | undefined {
    return store.getters["canva/continuation"] as string | undefined;
  }
  function designs(): canva.designs.Item[] {
    return store.getters["canva/designs"] as canva.designs.Item[];
  }
  function error(): string | null {
    return store.getters["canva/error"] as string | null;
  }
  function loading(): boolean {
    return store.getters["canva/loading"] as boolean;
  }
  function uploading(): boolean {
    return store.getters["canva/uploading"] as boolean;
  }
  function disabled(): boolean {
    return !selectedImage || !!error() || loading() || uploading();
  }
  function moreButtonText(): string {
    if (loading() && designs().length) {
      return "Loading...";
    }
    return "Load more...";
  }
  function submitButtonText(): string {
    if (uploading()) {
      return "Uploading";
    }
    return "Proceed";
  }

  async function handleDesignSelect(designId: string | null) {
    if (designId) store.dispatch("canva/selectDesign", designId);
  }

  function fetchDesigns() {
    store.dispatch("canva/fetchDesigns", continuation());
  }

  function getDesignAlt(design: canva.designs.Item): string {
    return design?.title || "Untitled Design";
  }
  function getDesignSrc(design: canva.designs.Item): string {
    return design?.thumbnail?.url;
  }

  useEffect(() => {
    store.commit("canva/$setModal", modalRef.current);
    fetchDesigns();
  }, []);

  return (
    <div
      {...restProps}
      className={cn("canva-modal modal scope-86c8d61d", restProps.className)}
      ref={modalRef}
      onClick={e => {
        e.stopPropagation();
      }}
      data-testid="canva-modal"
    >
      <div className="canva-modal__modal-box scope-86c8d61d">
        <div className="canva-modal__modal-header scope-86c8d61d">
          <div className="canva-modal__header scope-86c8d61d">
            <h3 className="canva-modal__title scope-86c8d61d">Choose image you want to upload:</h3>
            <button
              className="canva-modal__modal-close scope-86c8d61d"
              type="button"
              data-dismiss="modal"
              data-testid="canva-modal-modal-close"
            >
              <ModalCloseIcon className="scope-86c8d61d" data-testid="canva-modal-close-icon" />
            </button>
          </div>
        </div>
        <div className="canva-modal__modal-body scope-86c8d61d">
          {loading() && !designs().length ? (
            <div className="canva-modal__loading scope-86c8d61d"> Loading... </div>
          ) : error() ? (
            <div className="canva-modal__error scope-86c8d61d">{error()}</div>
          ) : !loading() && !designs().length ? (
            <div className="scope-86c8d61d">No images found</div>
          ) : (
            <div
              className={cn(
                "canva-modal__gallery scope-86c8d61d",
                uploading() && "canva-modal__gallery--uploading"
              )}
            >
              {renderList(designs(), design => (
                <a
                  className={cn(
                    "canva-modal__gallery-item scope-86c8d61d",
                    design.id === selectedImage && "canva-modal__gallery-item--selected"
                  )}
                  key={design.id}
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    setSelectedImage(design.id);
                  }}
                  data-testid="canva-modal-gallery-item"
                >
                  <img
                    className={cn(
                      "canva-modal__gallery-image scope-86c8d61d",
                      !getDesignSrc(design) && "canva-modal__gallery-image--placeholder"
                    )}
                    alt={getDesignAlt(design)}
                    src={getDesignSrc(design)}
                  />
                  {design.id === selectedImage ? (
                    <CheckCircleBoldIcon
                      className="canva-modal__gallery-check scope-86c8d61d"
                      data-testid="canva-modal-gallery-check"
                    />
                  ) : null}
                </a>
              ))}
            </div>
          )}
        </div>
        <div className="canva-modal__actions scope-86c8d61d">
          {continuation() ? (
            <button
              className="canva-modal__action canva-modal__action--secondary scope-86c8d61d"
              disabled={loading() || uploading()}
              type="button"
              onClick={() => {
                fetchDesigns();
              }}
              data-testid="canva-modal-action"
            >
              {moreButtonText()}
            </button>
          ) : null}
          <button
            className="canva-modal__action canva-modal__action--primary scope-86c8d61d"
            disabled={disabled()}
            type="button"
            onClick={() => {
              handleDesignSelect(selectedImage);
            }}
            data-testid="canva-modal-action-1"
          >
            {submitButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
}
